import { useCallback, useRef, useState, type ClipboardEvent, type DragEvent, type KeyboardEvent } from "react";
import { Button, Tooltip } from "antd";
import { AudioOutlined, CloseOutlined, FileOutlined, PlusOutlined, SendOutlined } from "@ant-design/icons";
import { useT } from "../i18n/context";

interface Attachment {
  file: File;
  preview?: string;
  /** 上传后由 onUpload 返回的 URL；未上传则为 undefined */
  url?: string;
}

interface ComposerProps {
  disabled?: boolean;
  placeholder?: string;
  onSend: (content: string, attachmentUrls: string[]) => void;
  onUploadAttachment?: (file: File) => Promise<string>;
  onUploadError?: (file: File, cause: Error) => void;
}

export default function Composer({
  disabled,
  placeholder,
  onSend,
  onUploadAttachment,
  onUploadError,
}: ComposerProps) {
  const t = useT();
  const [input, setInput] = useState("");
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const addFiles = useCallback(
    (files: FileList | File[]) => {
      const arr = Array.from(files);
      arr.forEach((file) => {
        if (file.type.startsWith("image/")) {
          const reader = new FileReader();
          reader.onload = (e) => {
            setAttachments((prev) => {
              const exists = prev.some((a) => a.file.name === file.name && a.file.size === file.size);
              if (exists) return prev;
              return [...prev, { file, preview: e.target?.result as string }];
            });
          };
          reader.readAsDataURL(file);
        } else {
          setAttachments((prev) => {
            const exists = prev.some((a) => a.file.name === file.name && a.file.size === file.size);
            if (exists) return prev;
            return [...prev, { file }];
          });
        }
      });
    },
    [],
  );

  const removeAttachment = (index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSend = async () => {
    if (!input.trim() || disabled) return;
    const content = input.trim();
    let urls: string[] = [];
    if (attachments.length > 0 && onUploadAttachment) {
      setUploading(true);
      try {
        const results = await Promise.all(
          attachments.map(async (a) => {
            if (a.url) return a.url;
            try {
              return await onUploadAttachment(a.file);
            } catch (e) {
              const err = e instanceof Error ? e : new Error(String(e));
              onUploadError?.(a.file, err);
              return null;
            }
          }),
        );
        urls = results.filter((v): v is string => !!v);
      } finally {
        setUploading(false);
      }
    }
    onSend(content, urls);
    setInput("");
    setAttachments([]);
    const ta = textareaRef.current;
    if (ta) ta.style.height = "auto";
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      void handleSend();
    }
  };

  const handlePaste = (e: ClipboardEvent<HTMLTextAreaElement>) => {
    const files = e.clipboardData.files;
    if (files && files.length > 0) {
      e.preventDefault();
      addFiles(files);
    }
  };

  const handleDragOver = (e: DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };
  const handleDragLeave = (e: DragEvent) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setIsDragging(false);
    }
  };
  const handleDrop = (e: DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const files = e.dataTransfer.files;
    if (files && files.length > 0) addFiles(files);
  };

  const canSend = !!input.trim() && !disabled && !uploading;

  return (
    <div
      style={{ padding: "4px 12px 8px" }}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <div
        style={{
          border: `1px solid ${isDragging ? "var(--lude-im-accent)" : "var(--lude-im-border)"}`,
          borderRadius: "var(--lude-im-radius-bubble)",
          background: "var(--lude-im-bg-input)",
          transition: "border-color 0.2s",
          overflow: "hidden",
          opacity: disabled ? 0.6 : 1,
        }}
      >
        {attachments.length > 0 && (
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 8,
              padding: "10px 12px 0",
            }}
          >
            {attachments.map((att, idx) => (
              <div
                key={idx}
                style={{
                  position: "relative",
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  background: "var(--lude-im-bg-secondary)",
                  border: "1px solid var(--lude-im-border)",
                  borderRadius: "var(--lude-im-radius-card)",
                  padding: "4px 8px",
                  maxWidth: 200,
                }}
              >
                {att.preview ? (
                  <img
                    src={att.preview}
                    alt={att.file.name}
                    style={{
                      width: 40,
                      height: 40,
                      objectFit: "cover",
                      borderRadius: 4,
                      flexShrink: 0,
                    }}
                  />
                ) : (
                  <FileOutlined
                    style={{
                      fontSize: 20,
                      color: "var(--lude-im-text-secondary)",
                      flexShrink: 0,
                    }}
                  />
                )}
                <div style={{ minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 500,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      color: "var(--lude-im-text-primary)",
                    }}
                  >
                    {att.file.name}
                  </div>
                  <div style={{ fontSize: 11, color: "var(--lude-im-text-secondary)" }}>
                    {fmtSize(att.file.size)}
                  </div>
                </div>
                <button
                  onClick={() => removeAttachment(idx)}
                  style={{
                    position: "absolute",
                    top: -6,
                    right: -6,
                    width: 16,
                    height: 16,
                    borderRadius: "50%",
                    background: "var(--lude-im-text-secondary)",
                    border: "none",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: 0,
                    color: "#fff",
                    fontSize: 10,
                    lineHeight: 1,
                  }}
                >
                  <CloseOutlined style={{ fontSize: 8 }} />
                </button>
              </div>
            ))}
          </div>
        )}

        <textarea
          ref={textareaRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          disabled={disabled}
          placeholder={placeholder ?? t("chat.input_placeholder")}
          rows={1}
          style={{
            width: "100%",
            resize: "none",
            border: "none",
            outline: "none",
            background: "transparent",
            color: "var(--lude-im-text-primary)",
            fontSize: 13,
            lineHeight: 1.6,
            padding: "8px 12px",
            fontFamily: "inherit",
            maxHeight: 160,
            overflowY: "auto",
            boxSizing: "border-box",
          }}
          onInput={(e) => {
            const el = e.currentTarget;
            el.style.height = "auto";
            el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
          }}
        />

        <div
          style={{
            display: "flex",
            alignItems: "center",
            padding: "4px 8px 6px",
            gap: 4,
          }}
        >
          {onUploadAttachment && (
            <>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                style={{ display: "none" }}
                onChange={(e) => {
                  if (e.target.files) addFiles(e.target.files);
                  e.target.value = "";
                }}
              />
              <Tooltip title={t("attachment.add")}>
                <Button
                  type="text"
                  icon={<PlusOutlined />}
                  size="small"
                  disabled={disabled}
                  style={{ color: "var(--lude-im-text-secondary)" }}
                  onClick={() => fileInputRef.current?.click()}
                />
              </Tooltip>
            </>
          )}

          <div style={{ flex: 1 }} />

          <Tooltip title={t("attachment.voice")}>
            <Button
              type="text"
              icon={<AudioOutlined />}
              size="small"
              style={{ color: "var(--lude-im-text-secondary)" }}
              disabled
            />
          </Tooltip>
          <Button
            type="primary"
            icon={<SendOutlined />}
            size="small"
            shape="circle"
            loading={uploading}
            disabled={!canSend}
            onClick={() => void handleSend()}
            style={!canSend ? { background: "var(--lude-im-border)", borderColor: "var(--lude-im-border)", color: "var(--lude-im-text-secondary)" } : {}}
          />
        </div>
      </div>
    </div>
  );
}

function fmtSize(size: number): string {
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / 1024 / 1024).toFixed(1)} MB`;
}
